import { Fragment, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Link } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { FiCalendar, FiUsers, FiTag, FiX } from "react-icons/fi";

export default function ExploreModal({ props, children }) {
  const [open, setOpen] = useState(false);
  const cancelButtonRef = useRef(null);
  const { user } = useAuth0();

  const publishedDate = new Date(props.datePublished);

  function formatDate(string) {
    var options = { year: "numeric", month: "long", day: "numeric" };
    return new Date(string).toLocaleDateString([], options);
  }

  const isCreator = user && user.sub === props.userID;
  const hasPurchased =
    user && props.purchasedBy && props.purchasedBy.includes(user.sub);

  return (
    <>
      <div onClick={() => setOpen(true)}>{children}</div>

      <Transition.Root show={open} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          initialFocus={cancelButtonRef}
          onClose={setOpen}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-3xl">
                  <button
                    type="button"
                    className="absolute top-4 right-4 z-10 rounded-full p-1 text-gray-400 hover:text-white hover:bg-black/20 focus:outline-none focus:ring-2 focus:ring-white"
                    onClick={() => setOpen(false)}
                  >
                    <span className="sr-only">Close</span>
                    <FiX className="h-6 w-6" />
                  </button>

                  {/* Image Section */}
                  <div className="relative h-64 sm:h-80">
                    <img
                      src={props.imgUrl}
                      className="w-full h-full object-cover"
                      alt={props.title}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                    <div className="absolute bottom-0 left-0 p-6">
                      <Dialog.Title className="text-3xl font-extrabold text-white drop-shadow-lg">
                        {props.title}
                      </Dialog.Title>
                    </div>
                  </div>

                  {/* Content Section */}
                  <div className="px-6 py-6 sm:px-8">
                    <div className="space-y-6">
                      {/* Description */}
                      <p className="text-lg text-gray-600 dark:text-gray-300">
                        {props.description || props.previewText}
                      </p>

                      {/* Categories */}
                      {props.categories && props.categories.length > 0 && (
                        <div className="flex flex-wrap gap-3 items-center">
                          <FiTag className="text-gray-400" />
                          {props.categories.map((category) => (
                            <span
                              key={category}
                              className="px-3 py-1 bg-primary-100 text-primary-800 rounded-full text-sm font-medium"
                            >
                              {category}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Stats Grid */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 py-6 border-y border-gray-200 dark:border-gray-700">
                        <div className="flex items-start space-x-4">
                          <FiCalendar className="w-6 h-6 text-primary-500 flex-shrink-0 mt-1" />
                          <div>
                            <p className="font-semibold text-gray-900 dark:text-white">
                              {props.endDate.none
                                ? "No Deadline"
                                : "Ends in X days"}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              Posted {formatDate(publishedDate)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start space-x-4">
                          <FiUsers className="w-6 h-6 text-primary-500 flex-shrink-0 mt-1" />
                          <div>
                            <p className="font-semibold text-gray-900 dark:text-white">
                              {props.quantity.unlimited
                                ? "Unlimited Spots"
                                : `${
                                    props.quantity.quantity -
                                    props.quantity.currentEnvolves
                                  } / ${props.quantity.quantity} Spots Left`}
                            </p>
                            <p className="text-2xl font-bold text-primary-500 mt-1">
                              {props.price.free
                                ? "Free to Join"
                                : `${props.price.price}`}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* User Info */}
                      <Link
                        to={`/profile/${
                          props.userID?.includes("|")
                            ? props.userID.split("|")[1]
                            : props.userID
                        }`}
                        className="flex items-center space-x-4 group"
                      >
                        <img
                          src={props.userPicture}
                          className="w-14 h-14 rounded-full object-cover ring-4 ring-white dark:ring-gray-800 group-hover:ring-primary-500 transition-all duration-300"
                          alt={props.username}
                        />
                        <div>
                          <p className="text-lg font-bold text-gray-900 dark:text-white group-hover:text-primary-500 transition-colors">
                            {props.username}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Envolvement Creator
                          </p>
                        </div>
                      </Link>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="bg-gray-50 dark:bg-gray-900/50 px-6 py-4 flex flex-col sm:flex-row-reverse sm:items-center gap-3">
                    {isCreator ? (
                      <div className="w-full sm:w-auto inline-flex justify-center items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-gray-500 cursor-not-allowed">
                        This is your Envolvement
                      </div>
                    ) : hasPurchased ? (
                      <div className="w-full sm:w-auto inline-flex justify-center items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-green-600 cursor-not-allowed">
                        Already Joined
                      </div>
                    ) : (
                      <Link
                        to={`/payment/${props._id}`}
                        className="w-full sm:w-auto inline-flex justify-center items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                      >
                        {props.price.free
                          ? "Join for Free"
                          : `Pay ${props.price.price}`}
                      </Link>
                    )}
                    <Link
                      to={`/envolvement/${props._id}`}
                      className="w-full sm:w-auto inline-flex justify-center items-center px-6 py-3 border border-gray-300 dark:border-gray-600 text-base font-medium rounded-lg text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                    >
                      View Details
                    </Link>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
}
